import * as React from 'react';
import { MdAdd } from 'react-icons/md';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { AddButton, ElementLoader } from 'ui';
import { STATE } from 'app/constants';
import { Timer } from 'app/components/timer/timer';
import { actionCreators } from './actions';
import { importTranslations, importStatusPollingInterval } from './constants';
import { ImportViewItem } from './import-view-item';
import { ImportFormData } from './types';
import { ImportState } from './types-store';
import { ImportAddCase } from './import-add-case';
import { ImportEditCase } from './import-edit-case';

import styles from './import-view.scss';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { Waypoint } from 'react-waypoint';
import { scrollToTop } from 'app/infrastructure/platform/routing';

interface ImportViewComponentState {
  showImportAddCase: boolean;
}

export type ImportViewComponentProps = ImportState & DefaultContainerProps & typeof actionCreators;

class ImportViewComponent extends React.Component<ImportViewComponentProps, ImportViewComponentState> {
  public static statePath: string = STATE.import;

  public state: Readonly<ImportViewComponentState> = {
    showImportAddCase: false
  };

  public componentDidMount() {
    scrollToTop();
    this.props.loadImportList();
  }

  private handleAddFile = () => {
    this.setState({
      showImportAddCase: true
    });
  };

  private handleClose = () => {
    this.setState({
      showImportAddCase: false
    });
    this.props.clearImportCase();
  };

  private handleShowEditImportCase = (id: number) => {

    this.props.loadEditImport(id);
  };

  private handleCloseEditModal = (id: number) => {
    this.props.clearEditImport(id);
  };

  private updateImport = (importFormData: ImportFormData) => {
    this.props.updateImport(this.props.importCaseToEdit.id, importFormData);
  };

  private renderImportList = () => {
    const {
      importList,
      updatedImportCases,
      fetchingImportList,
      fetchingMoreImportListItems,
      loadMoreImportList,
      reachedEndOfList
    } = this.props;

    const apiCallInProgress = fetchingImportList || fetchingMoreImportListItems;

    return (
      <>
        {[...importList].map((item, index) =>
          <div key={index} className={'col-md-12'}>
            <ImportViewItem
              {...this.props}
              item={item}
              fetchingItem={item.id === this.props.editedImportId || updatedImportCases.some(id => id === item.id)}
              canEditItem={this.props.editedImportId === undefined}
              handleEdit={this.handleShowEditImportCase}
            />
          </div>)}
        {!apiCallInProgress && !reachedEndOfList && <Waypoint
          key={`wp${importList.length}`}
          onEnter={loadMoreImportList}
          bottomOffset="-10%" />}
        {fetchingMoreImportListItems && <ElementLoader />}
      </>
    );
  };

  private renderImportAddModal() {
    return this.state.showImportAddCase
      && <ImportAddCase
        {...this.props}
        fetching={this.props.fetchingAddImportCase}
        onClose={this.handleClose}
        onDone={this.props.saveImport} />;
  }

  private renderImportEditModal() {
    const { importCaseToEdit } = this.props;

    return importCaseToEdit && <ImportEditCase
      {...this.props}
      importCase={importCaseToEdit}
      onClose={this.handleCloseEditModal}
      onDone={this.updateImport} />;
  }

  public render() {
    const { importCaseToEdit, importList, translation, editedImportId, updatedImportCases } = this.props;
    const pendingApiOperations = importCaseToEdit || editedImportId !== undefined || updatedImportCases.length > 0;
    const displayImportList = importList && importList.length > 0
      ? this.renderImportList()
      : (!this.props.importList
        ? <ElementLoader />
        : <div className="col-md-12">{translation.no_imports}</div>);

    return (<MainGridContainer className={styles.importContainer}>
      <div className="page-heading"><h1>{translation.import_title}</h1></div>

      <div className={styles.tileSection}>
        <div className="mb-2">
          <AddButton icon={MdAdd} onClick={this.handleAddFile}>
            {translation.import_add_to_import}
          </AddButton>
        </div>

        <div className="row">
          {!pendingApiOperations ?
            <Timer
              callback={this.props.loadImportList}
              interval={importStatusPollingInterval}>
              {displayImportList}
            </Timer> :
            displayImportList}
        </div>
      </div>

      {this.renderImportAddModal()}
      {this.renderImportEditModal()}
    </MainGridContainer>);
  }
}

const container = containerFactory<ImportViewComponentProps>(ImportViewComponent, actionCreators, undefined, importTranslations);
export { container as ImportView };
